/**
 * Login Tracker
 * =============
 * Captures login context for audit and security monitoring:
 *   - Timestamp
 *   - IP address + coarse geolocation (via free ipapi.co endpoint, fallback: ipify)
 *   - Browser / OS / device
 *   - Screen resolution
 *   - Browser language + timezone
 *
 * Storage:
 *   - Mock / demo mode → localStorage per-user, capped to LATEST_N entries
 *   - Backend mode    → POST /audit/login-event (implementation lives on the
 *     server; this module only makes a best-effort call — failures are swallowed
 *     so auth flow is never blocked)
 *
 * Privacy: IP + UA + coarse location only. No PHI. Entries age out after
 * DEFAULT_RETENTION_DAYS (default 90 days) on read.
 */

import { authApi } from '../api/authApi';

const STORAGE_PREFIX = 'sendero_login_history_';
const LATEST_N = 25;
const DEFAULT_RETENTION_DAYS = 90;
const IP_FETCH_TIMEOUT_MS = 1500;

/* ── Browser / OS / device parsing from userAgent ──────────────── */
const parseUserAgent = (ua = navigator.userAgent || '') => {
    const browser =
        /edg\//i.test(ua) ? 'Edge' :
            /chrome\//i.test(ua) && !/chromium/i.test(ua) ? 'Chrome' :
                /firefox\//i.test(ua) ? 'Firefox' :
                    /safari\//i.test(ua) && !/chrome/i.test(ua) ? 'Safari' :
                        /opera|opr\//i.test(ua) ? 'Opera' :
                            'Unknown browser';

    const os =
        /windows nt 11/i.test(ua) ? 'Windows 11' :
            /windows nt 10/i.test(ua) ? 'Windows 10' :
                /windows nt/i.test(ua) ? 'Windows' :
                    /mac os x/i.test(ua) ? 'macOS' :
                        /android/i.test(ua) ? 'Android' :
                            /iphone|ipad|ipod/i.test(ua) ? 'iOS' :
                                /linux/i.test(ua) ? 'Linux' :
                                    'Unknown OS';

    const deviceType =
        /mobile|iphone|ipod/i.test(ua) ? 'Mobile' :
            /tablet|ipad/i.test(ua) ? 'Tablet' :
                'Desktop';

    return { browser, os, deviceType };
};

/* ── Best-effort IP + coarse location lookup ───────────────────── */
const withTimeout = (p, ms) =>
    Promise.race([
        p,
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
    ]);

async function fetchIpInfo() {
    // Primary: ipapi.co/json — returns IP + city + region + country + org
    
    try {
        const res = await withTimeout(
            fetch('https://ipapi.co/json/', { cache: 'no-store' }),
            IP_FETCH_TIMEOUT_MS,
        );
        
        if (res.ok) {           
            
            const data = await res.json();
            return {
                ip: data.ip || null,
                city: data.city || null,
                region: data.region || null,
                country: data.country_name || data.country || null,
                isp: data.org || null,
            };
        }
    } catch { /* ignore, fall through to secondary */ }
    
    // Secondary: api.ipify.org — IP only, very high availability
    try {
        const res = await withTimeout(
            fetch('https://api.ipify.org?format=json', { cache: 'no-store' }),
            IP_FETCH_TIMEOUT_MS,
        );
        if (res.ok) {
            const data = await res.json();
            return { ip: data.ip || null, city: null, region: null, country: null, isp: null };
        }
    } catch { /* ignore */ }

    return { ip: null, city: null, region: null, country: null, isp: null };
}

/* ── Capture full context ──────────────────────────────────────── */
export async function captureLoginContext() {
    
    const ua = parseUserAgent();
    
    let ipInfo = { ip: null, city: null, region: null, country: null, isp: null };
    try { ipInfo = await fetchIpInfo(); } catch {
        /* fallback already set */
        
        }


    
    return {
        timestamp: new Date().toISOString(),
        ...ipInfo,
        ...ua,
        userAgent: navigator.userAgent || '',
        language: navigator.language || '',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
        screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    };
}

/* ── Storage ───────────────────────────────────────────────────── */
function storageKey(userId) {
    return `${STORAGE_PREFIX}${userId || 'anonymous'}`;
}

function loadRaw(userId) {
    try {
        const raw = localStorage.getItem(storageKey(userId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
}

function saveRaw(userId, entries) {
    try {
        localStorage.setItem(storageKey(userId), JSON.stringify(entries));
    } catch { /* quota exceeded, ignore */ }
}

/**
 * Record a login event.
 *
 * @param {string|number} userId          Who this event belongs to. Use
 *                                        'failed-login' bucket for events
 *                                        where we don't have a user id.
 * @param {'success'|'mfa_required'|'mfa_failed'|'failed'|'logout'} type
 * @param {object}  [extra]               Extra context to merge in.
 * @returns {Promise<object>}             The stored entry.
 */
export async function recordLogin(userName, type, eventType, extra = {}) {
    var userId = "";
    //alert("HIT1");
    const ctx = await captureLoginContext();
    //alert("HIT2");
    const entry = {
        id: `${ctx.timestamp}-${Math.random().toString(36).slice(2, 8)}`,
        type,
        ...ctx,
        ...extra,
    };

    
    //console.log("---------REQUEST SEND WITH PARA-----------------");
    //console.log("Event Data :" + JSON.stringify(entry));
    //console.log("Event Type :" + type);
    //console.log("Username :" + userName);
    //console.log("-------------------------------------------------------------");
    
    
    const res = await authApi.recordEvent({
        userName: userName,
        eventType:eventType,
        eventData:entry
    });

    const bucket = userId || 'anonymous';
    const current = loadRaw(bucket);
    const next = [entry, ...current].slice(0, LATEST_N);
    saveRaw(bucket, next);

    return entry;
}

export async function recordImpersonation(userId, impersonatedUserId, type, eventType, extra = {}) {
    
    //alert("HIT1");

    console.log("--------------------------------------------");
    console.log("userId : " + userId);
    console.log("impersonatedUserId : " + impersonatedUserId);
    console.log("eventType : " + eventType);
    console.log("extra : " + extra);
    console.log("type : " + type);
    console.log("--------------------------------------------");
    const ctx = await captureLoginContext();
    //alert("HIT2");
    const entry = {
        id: `${ctx.timestamp}-${Math.random().toString(36).slice(2, 8)}`,
        type,
        ...ctx,
        ...extra,
    };



    const res = await authApi.recordImpersonation({
        userId: userId,
        impersonatedUserId: impersonatedUserId,
        eventType: eventType,
        eventData: entry
    });

    //const bucket = userId || 'anonymous';
    //const current = loadRaw(bucket);
    //const next = [entry, ...current].slice(0, LATEST_N);
    //saveRaw(bucket, next);

    return entry;
}

/**
 * Retrieve the login history for a user, sorted newest → oldest.
 * Entries older than `retentionDays` are filtered out (and never returned).
 */
export function getLoginHistory(userId, retentionDays = DEFAULT_RETENTION_DAYS) {
    const entries = loadRaw(userId);
    const cutoff = Date.now() - retentionDays * 86400000;
    return entries
        .filter((e) => new Date(e.timestamp).getTime() >= cutoff)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

/**
 * Clear the login history for a user (self-service "clear my activity" button).
 */
export function clearLoginHistory(userId) {
    try { localStorage.removeItem(storageKey(userId)); } catch { /* ignore */ }
}

/* ── Human-friendly formatting helpers ─────────────────────────── */
export function formatLocation(entry) {
    const parts = [entry.city, entry.region, entry.country].filter(Boolean);
    return parts.length ? parts.join(', ') : 'Location unknown';
}

export function formatDevice(entry) {
    const parts = [entry.browser, entry.os].filter(Boolean);
    return parts.length ? parts.join(' on ') : 'Unknown device';
}

export function formatRelative(iso) {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
    });
}

export const LOGIN_EVENT_TYPES = {
    success: { label: 'Signed in', color: '#059669', icon: 'check' },
    mfa_required: { label: 'MFA requested', color: '#0D9488', icon: 'shield' },
    mfa_failed: { label: 'Wrong MFA code', color: '#DC2626', icon: 'x' },
    failed: { label: 'Failed sign-in', color: '#DC2626', icon: 'x' },
    logout: { label: 'Signed out', color: '#6B7280', icon: 'logout' },
};


export const IMPERSONATION_EVENT_TYPES = {
    success: { label: 'Impersonation Started', color: '#059669', icon: 'check' },    
    failed: { label: 'Impersonation Failed', color: '#DC2626', icon: 'x' },
    logout: { label: 'Impersonation End', color: '#6B7280', icon: 'logout' },
};
